import React, { useState } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logger } from '../utils/logger';

// Esquema de validación
const RegisterSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email inválido')
    .required('Email es requerido'),
  password: Yup.string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .required('Contraseña es requerida'),
  phone_number: Yup.string()
    .required('Número de teléfono es requerido')
    .matches(/^\+?[0-9]{8,15}$/, 'Número de teléfono inválido'),
  full_name: Yup.string()
    .required('Nombre completo es requerido')
});

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Manejar el envío del formulario
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      setLoading(true);
      
      const result = await register({
        email: values.email,
        password: values.password,
        phone_number: values.phone_number,
        full_name: values.full_name
      });
      
      if (result.success) {
        // Redirigir a login después de un registro exitoso
        navigate('/login', { 
          state: { 
            message: "Registro exitoso. Ahora puede iniciar sesión." 
          } 
        });
      } else {
        setError(result.message || 'Error al registrarse');
      }
    } catch (err) {
      setError('Error al registrarse. Intente de nuevo más tarde.');
      logger.error(err);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <Card>
        <Card.Body>
          <h2 className="text-center mb-4">Registro</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Formik
            initialValues={{ 
              email: '', 
              password: '', 
              phone_number: '',
              full_name: '' 
            }}
            validationSchema={RegisterSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit} className="auth-form">
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={values.full_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.full_name && !!errors.full_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.full_name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Número de Teléfono</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone_number"
                    placeholder="+569XXXXXXXX"
                    value={values.phone_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.phone_number && !!errors.phone_number}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone_number}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Formato internacional (ej: +56912345678)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.password && !!errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 mt-3"
                  disabled={isSubmitting || loading}
                >
                  {loading ? 'Registrando...' : 'Registrarse'}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
      <div className="w-100 text-center mt-2">
        ¿Ya tienes una cuenta? <Link to="/login">Inicia sesión</Link>
      </div>
    </div>
  );
};

export default Register;